import React, { useMemo, useState } from 'react';
import {
  SparklesIcon,
  AcademicCapIcon,
  ChatBubbleLeftRightIcon,
  ArrowRightCircleIcon,
} from '@heroicons/react/24/outline';
import type {
  ObservationCitation,
  ReportNarrative,
  ReportNarrativeNextStep,
  ReportNarrativeSection,
} from '../../services/api/reports';

export interface ReportDraftViewerProps {
  draft: ReportNarrative;
  /** Fired with the (possibly edited) draft when the teacher saves. The
   * parent owns persistence -- this component is purely an editor. */
  onSave?: (edited: ReportNarrative) => void;
  /** Fire-and-forget back-out. Does not require save. */
  onClose?: () => void;
}

/**
 * Editable viewer for an AI-drafted term-report narrative.
 *
 * The draft is *always* a starting point: every section is a textarea so
 * the teacher can rewrite freely. Citations are rendered as chips next
 * to each section -- hovering a chip reveals the source observation
 * excerpt, building trust that the model isn't fabricating.
 */
const ReportDraftViewer: React.FC<ReportDraftViewerProps> = ({
  draft,
  onSave,
  onClose,
}) => {
  const [summary, setSummary] = useState(draft.summary);
  const [sections, setSections] = useState<ReportNarrativeSection[]>(draft.sections);
  const [nextSteps, setNextSteps] = useState<ReportNarrativeNextStep[]>(draft.next_steps);

  const observationsById = useMemo(() => {
    const map = new Map<string, ObservationCitation>();
    for (const o of draft.observations) map.set(o.id, o);
    return map;
  }, [draft.observations]);

  const updateSection = (idx: number, patch: Partial<ReportNarrativeSection>) => {
    setSections((prev) =>
      prev.map((s, i) => (i === idx ? { ...s, ...patch } : s)),
    );
  };

  const updateNextStep = (idx: number, text: string) => {
    setNextSteps((prev) =>
      prev.map((step, i) => (i === idx ? { ...step, text } : step)),
    );
  };

  const isEmpty =
    summary.trim() === '' && sections.length === 0 && nextSteps.length === 0;

  return (
    <div className="space-y-6" data-testid="report-draft-viewer">
      <header className="flex items-start justify-between gap-4">
        <div>
          <div className="flex items-center gap-2 text-emerald-700 dark:text-emerald-300 text-xs uppercase tracking-wide font-semibold">
            <SparklesIcon className="w-4 h-4" aria-hidden="true" />
            AI draft — review before saving
          </div>
          <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100 mt-1">
            {draft.child_name}
          </h2>
          <p className="text-sm text-gray-500 dark:text-gray-400">
            {new Date(draft.period_start).toLocaleDateString()} —{' '}
            {new Date(draft.period_end).toLocaleDateString()} ·{' '}
            {draft.observations_considered} observation
            {draft.observations_considered === 1 ? '' : 's'},{' '}
            {draft.assessments_considered} assessment
            {draft.assessments_considered === 1 ? '' : 's'} considered
          </p>
        </div>
      </header>

      {isEmpty ? (
        <div
          className="rounded-lg border border-amber-200 dark:border-amber-800 bg-amber-50 dark:bg-amber-900/20 p-4 text-sm text-amber-900 dark:text-amber-100"
          data-testid="report-draft-empty"
        >
          The AI couldn't draft a narrative for this period — there may be
          no observations, or the local LLM is unreachable. You can still
          write the report manually.
        </div>
      ) : (
        <>
          {/* Summary */}
          <section data-testid="report-draft-summary-section">
            <label className="block text-xs uppercase tracking-wide font-semibold text-gray-600 dark:text-gray-400 mb-2">
              Summary
            </label>
            <textarea
              value={summary}
              onChange={(e) => setSummary(e.target.value)}
              rows={3}
              data-testid="report-draft-summary"
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-700 rounded-md bg-white dark:bg-gray-800 text-sm text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-1 focus:ring-emerald-500 focus:border-emerald-500"
            />
          </section>

          {/* Per-area sections */}
          {sections.length > 0 && (
            <section className="space-y-4" data-testid="report-draft-sections">
              <label className="block text-xs uppercase tracking-wide font-semibold text-gray-600 dark:text-gray-400">
                Per Learning Area
              </label>
              {sections.map((section, idx) => (
                <div
                  key={section.area_id || idx}
                  className="rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 p-4 space-y-3"
                >
                  <div className="flex items-center gap-2">
                    <AcademicCapIcon
                      className="w-4 h-4 text-emerald-600 dark:text-emerald-400"
                      aria-hidden="true"
                    />
                    <span className="text-sm font-semibold text-gray-900 dark:text-gray-100">
                      {section.area_name}
                    </span>
                  </div>
                  <textarea
                    value={section.narrative}
                    onChange={(e) =>
                      updateSection(idx, { narrative: e.target.value })
                    }
                    rows={3}
                    data-testid={`report-draft-section-${idx}`}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-700 rounded-md bg-white dark:bg-gray-800 text-sm text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-1 focus:ring-emerald-500 focus:border-emerald-500"
                  />

                  {section.evidence_ids.length > 0 && (
                    <div className="flex flex-wrap gap-1.5">
                      <span className="text-xs text-gray-500 dark:text-gray-400 self-center">
                        Cited:
                      </span>
                      {section.evidence_ids.map((id) => {
                        const obs = observationsById.get(id);
                        if (!obs) return null;
                        return (
                          <span
                            key={id}
                            title={obs.excerpt}
                            data-testid={`citation-${id}`}
                            className="inline-flex items-center gap-1 px-2 py-0.5 text-xs rounded-full border border-emerald-200 dark:border-emerald-800 bg-emerald-50 dark:bg-emerald-900/20 text-emerald-800 dark:text-emerald-200 cursor-help"
                          >
                            <ChatBubbleLeftRightIcon
                              className="w-3 h-3"
                              aria-hidden="true"
                            />
                            {new Date(obs.observation_date).toLocaleDateString()}
                          </span>
                        );
                      })}
                    </div>
                  )}
                </div>
              ))}
            </section>
          )}

          {/* Next steps */}
          {nextSteps.length > 0 && (
            <section className="space-y-2" data-testid="report-draft-next-steps">
              <label className="block text-xs uppercase tracking-wide font-semibold text-gray-600 dark:text-gray-400">
                Next steps
              </label>
              <ul className="space-y-2">
                {nextSteps.map((step, idx) => (
                  <li
                    key={idx}
                    className="flex items-start gap-2 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 p-3"
                  >
                    <ArrowRightCircleIcon
                      className="w-4 h-4 mt-1 text-emerald-600 dark:text-emerald-400 flex-shrink-0"
                      aria-hidden="true"
                    />
                    <textarea
                      value={step.text}
                      onChange={(e) => updateNextStep(idx, e.target.value)}
                      rows={2}
                      data-testid={`report-draft-step-${idx}`}
                      className="flex-1 px-2 py-1 border border-transparent hover:border-gray-300 focus:border-emerald-500 focus:ring-1 focus:ring-emerald-500 dark:hover:border-gray-600 rounded-md bg-transparent text-sm text-gray-900 dark:text-gray-100 focus:outline-none resize-none"
                    />
                  </li>
                ))}
              </ul>
            </section>
          )}
        </>
      )}

      {/* Actions */}
      <div className="flex items-center justify-end gap-2 border-t border-gray-200 dark:border-gray-700 pt-4">
        {onClose && (
          <button
            type="button"
            onClick={onClose}
            className="px-3 py-1.5 text-sm font-medium rounded-md border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
          >
            Close
          </button>
        )}
        {onSave && !isEmpty && (
          <button
            type="button"
            data-testid="report-draft-save"
            onClick={() =>
              onSave({
                ...draft,
                summary,
                sections,
                next_steps: nextSteps,
              })
            }
            className="px-3 py-1.5 text-sm font-medium rounded-md bg-emerald-600 text-white hover:bg-emerald-700 transition-colors"
          >
            Save draft
          </button>
        )}
      </div>
    </div>
  );
};

export default ReportDraftViewer;
